import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Tag, Typography } from 'antd';
import { DeleteOutlined, UndoOutlined, ZoomInOutlined, ZoomOutOutlined, ExpandOutlined } from '@ant-design/icons';

const { Text } = Typography;

// ─── Types ────────────────────────────────────────────────────────────────────

export interface SlamMapData {
    id: number;
    name: string;
    imageUrl: string;
    imageWidth?: number | null;
    imageHeight?: number | null;
    metersPerPixel?: number | null;
    originX?: number | null;
    originY?: number | null;
    rotationDegrees?: number | null;
    geoAnchor?: {
        lat: number;
        lng: number;
        anchorPixelX: number;
        anchorPixelY: number;
        bearingDegrees: number;
    } | null;
}

export interface IndoorGeomResult {
    gpsGeom: any | null;
    localGeom: any | null;
}

export interface IndoorAreaMapProps {
    slamMap: SlamMapData;
    mode: 'polygon' | 'point';
    templateImageUrl?: string | null;
    templateDimensions?: { widthMeters: number; heightMeters: number } | null;
    existingGeom?: any | null;
    manualAnchor?: { lat: number; lng: number; bearingDegrees: number } | null;
    maxVertices?: number | null;
    canDraw?: boolean;
    markers?: IndoorMapMarker[];
    onMarkerSelect?: (marker: IndoorMapMarker) => void;
    onClear?: () => void;
    onGeomChange: (result: IndoorGeomResult | null) => void;
}

interface Pt { x: number; y: number; }
export interface IndoorMapMarker {
    id: number | string;
    label?: string | null;
    categoryKey?: string | null;
    localPose?: { x: number; y: number; [key: string]: any } | null;
}

function hasDrawableGeom(geom: any): boolean {
    if (!geom) return false;
    if (geom.type === 'Point') return Array.isArray(geom.coordinates) && geom.coordinates.length >= 2;
    if (geom.type === 'Polygon') return Array.isArray(geom.coordinates?.[0]) && geom.coordinates[0].length >= 4;
    return false;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const MIN_ZOOM = 0.15;
const MAX_ZOOM = 5;
const ZOOM_STEP = 0.15;
const CORNER_RADIUS = 8;
const CORNER_HIT_RADIUS = 16;
const WARP_SUBDIVISIONS = 8; // Grid size for perspective warp approximation

// ─── Coordinate math (pixels ↔ metres ↔ GPS) ─────────────────────────────────

const DEG_TO_RAD = Math.PI / 180;
const EARTH_RADIUS = 6371000;

function pixelToMeters(px: number, py: number, mpp: number, ax: number, ay: number) {
    return { mx: (px - ax) * mpp, my: (ay - py) * mpp };
}

function metersToPixel(mx: number, my: number, mpp: number, ax: number, ay: number): Pt {
    return { x: ax + mx / mpp, y: ay - my / mpp };
}

function metersToGps(mx: number, my: number, aLat: number, aLng: number, bDeg: number) {
    const b = bDeg * DEG_TO_RAD;
    const nM = my * Math.cos(b) - mx * Math.sin(b);
    const eM = my * Math.sin(b) + mx * Math.cos(b);
    return {
        lat: aLat + (nM / EARTH_RADIUS) * (180 / Math.PI),
        lng: aLng + (eM / (EARTH_RADIUS * Math.cos(aLat * DEG_TO_RAD))) * (180 / Math.PI),
    };
}

function gpsToPixel(lat: number, lng: number, mpp: number, ax: number, ay: number,
    aLat: number, aLng: number, bDeg: number): Pt {
    const dlat = (lat - aLat) * DEG_TO_RAD * EARTH_RADIUS;
    const dlng = (lng - aLng) * DEG_TO_RAD * EARTH_RADIUS * Math.cos(aLat * DEG_TO_RAD);
    const b = bDeg * DEG_TO_RAD;
    const my = dlat * Math.cos(b) + dlng * Math.sin(b);
    const mx = -dlat * Math.sin(b) + dlng * Math.cos(b);
    return metersToPixel(mx, my, mpp, ax, ay);
}

// ─── Component ────────────────────────────────────────────────────────────────

const IndoorAreaMap: React.FC<IndoorAreaMapProps> = ({
    slamMap, mode, templateImageUrl, templateDimensions, existingGeom, manualAnchor, maxVertices, canDraw = true, markers = [], onMarkerSelect, onClear, onGeomChange,
}) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    // Images loaded into memory
    const slamImgRef = useRef<HTMLImageElement | null>(null);
    const templateImgRef = useRef<HTMLImageElement | null>(null);
    const [slamLoaded, setSlamLoaded] = useState(false);
    const [templateLoaded, setTemplateLoaded] = useState(false);

    // Drawing state — all coordinates are in SLAM-image native pixels
    const [polygonPoints, setPolygonPoints] = useState<Pt[]>([]);
    const [pointLocation, setPointLocation] = useState<Pt | null>(null);

    // Interaction state
    const [draggingIdx, setDraggingIdx] = useState<number | null>(null);
    const [isPanning, setIsPanning] = useState(false);
    const panStartRef = useRef({ x: 0, y: 0, panX: 0, panY: 0 });

    // View transform: pan (in screen pixels) and zoom
    const [zoom, setZoom] = useState(1);
    const [pan, setPan] = useState<Pt>({ x: 0, y: 0 });

    const templateAutoPlacedRef = useRef(false);

    // Derived constants
    const mpp = slamMap.metersPerPixel ?? 0.05;
    const isAnchored = !!slamMap.geoAnchor;
    const anchorPx = slamMap.geoAnchor?.anchorPixelX ?? (slamMap.imageWidth ?? 800) / 2;
    const anchorPy = slamMap.geoAnchor?.anchorPixelY ?? (slamMap.imageHeight ?? 600) / 2;

    const effectiveAnchor = useMemo(() => {
        if (slamMap.geoAnchor) return { lat: slamMap.geoAnchor.lat, lng: slamMap.geoAnchor.lng, bearingDegrees: slamMap.geoAnchor.bearingDegrees };
        if (manualAnchor) return manualAnchor;
        return null;
    }, [slamMap.geoAnchor, manualAnchor]);

    const isTemplateLocked = !!(templateImageUrl && mode === 'polygon' && polygonPoints.length > 0);
    const markerPoints = useMemo(() => (
        markers
            .map((marker) => {
                const x = Number(marker.localPose?.x);
                const y = Number(marker.localPose?.y);
                if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
                return { marker, native: metersToPixel(x, y, mpp, anchorPx, anchorPy) };
            })
            .filter(Boolean) as Array<{ marker: IndoorMapMarker; native: Pt }>
    ), [markers, mpp, anchorPx, anchorPy]);

    // ── Load SLAM image ───────────────────────────────────────────────────────

    useEffect(() => {
        const img = new Image();
        img.onload = () => { slamImgRef.current = img; setSlamLoaded(true); };
        img.onerror = (e) => { console.error('[IndoorAreaMap] Failed to load SLAM image:', slamMap.imageUrl, e); };
        img.src = slamMap.imageUrl;
        return () => { img.onload = null; img.onerror = null; };
    }, [slamMap.imageUrl]);

    // ── Load template image ───────────────────────────────────────────────────

    useEffect(() => {
        if (!templateImageUrl) { templateImgRef.current = null; setTemplateLoaded(false); return; }
        const img = new Image();
        img.onload = () => { templateImgRef.current = img; setTemplateLoaded(true); };
        img.onerror = (e) => { console.error('[IndoorAreaMap] Failed to load template image:', templateImageUrl, e); };
        img.src = templateImageUrl;
        return () => { img.onload = null; img.onerror = null; };
    }, [templateImageUrl]);

    // ── Coordinate helpers ────────────────────────────────────────────────────

    /** Screen mouse position → native SLAM-image pixel coords */
    const screenToNative = useCallback((clientX: number, clientY: number): Pt => {
        const canvas = canvasRef.current;
        if (!canvas) return { x: 0, y: 0 };
        const rect = canvas.getBoundingClientRect();
        const sx = (clientX - rect.left);
        const sy = (clientY - rect.top);
        // Reverse the transform: screen = (native * zoom) + pan
        return { x: (sx - pan.x) / zoom, y: (sy - pan.y) / zoom };
    }, [zoom, pan]);

    /** Native pixel → output coord (local metres) */
    const nativeToLocal = useCallback((px: number, py: number): [number, number] => {
        const { mx, my } = pixelToMeters(px, py, mpp, anchorPx, anchorPy);
        return [mx, my];
    }, [mpp, anchorPx, anchorPy]);

    /** Native pixel → GPS [lng, lat] or null */
    const nativeToGps = useCallback((px: number, py: number): [number, number] | null => {
        if (!effectiveAnchor) return null;
        const { mx, my } = pixelToMeters(px, py, mpp, anchorPx, anchorPy);
        const { lat, lng } = metersToGps(mx, my, effectiveAnchor.lat, effectiveAnchor.lng, effectiveAnchor.bearingDegrees);
        return [lng, lat];
    }, [effectiveAnchor, mpp, anchorPx, anchorPy]);

    /** Output coord → native pixel */
    const outputToNative = useCallback((coord: [number, number]): Pt => {
        if (isAnchored && slamMap.geoAnchor) {
            const [lng, lat] = coord;
            return gpsToPixel(lat, lng, mpp, anchorPx, anchorPy,
                slamMap.geoAnchor.lat, slamMap.geoAnchor.lng, slamMap.geoAnchor.bearingDegrees);
        }
        const [mx, my] = coord;
        return metersToPixel(mx, my, mpp, anchorPx, anchorPy);
    }, [isAnchored, slamMap.geoAnchor, mpp, anchorPx, anchorPy]);

    // ── Emit geometry ─────────────────────────────────────────────────────────

    const emitGeom = useCallback((pts: Pt[], pointLoc: Pt | null) => {
        if (mode === 'point') {
            if (!pointLoc) { onGeomChange(null); return; }
            onGeomChange({
                localGeom: { type: 'Point', coordinates: nativeToLocal(pointLoc.x, pointLoc.y) },
                gpsGeom: nativeToGps(pointLoc.x, pointLoc.y) ? { type: 'Point', coordinates: nativeToGps(pointLoc.x, pointLoc.y) } : null,
            });
        } else {
            if (pts.length < 3) { onGeomChange(null); return; }
            const localRing: [number, number][] = [];
            const gpsRing: [number, number][] | null = effectiveAnchor ? [] : null;
            for (const p of pts) {
                localRing.push(nativeToLocal(p.x, p.y));
                if (gpsRing) { const g = nativeToGps(p.x, p.y); if (g) gpsRing.push(g); }
            }
            localRing.push(localRing[0]);
            if (gpsRing) gpsRing.push(gpsRing[0]);
            onGeomChange({
                localGeom: { type: 'Polygon', coordinates: [localRing] },
                gpsGeom: gpsRing ? { type: 'Polygon', coordinates: [gpsRing] } : null,
            });
        }
    }, [mode, onGeomChange, nativeToLocal, nativeToGps, effectiveAnchor]);

    useEffect(() => { emitGeom(polygonPoints, pointLocation); }, [polygonPoints, pointLocation, emitGeom]);

    // ── Load existing geometry ────────────────────────────────────────────────

    useEffect(() => {
        if (!existingGeom || !slamLoaded) return;
        if (existingGeom.type === 'Point') {
            const [cx, cy] = existingGeom.coordinates;
            const native = isAnchored && slamMap.geoAnchor
                ? gpsToPixel(cy, cx, mpp, anchorPx, anchorPy, slamMap.geoAnchor.lat, slamMap.geoAnchor.lng, slamMap.geoAnchor.bearingDegrees)
                : metersToPixel(cx, cy, mpp, anchorPx, anchorPy);
            setPointLocation(native);
        } else if (existingGeom.type === 'Polygon') {
            const coords: [number, number][] = existingGeom.coordinates[0] || [];
            setPolygonPoints(coords.slice(0, -1).map(c => outputToNative(c)));
        }
    }, [existingGeom, slamLoaded, isAnchored, mpp, anchorPx, anchorPy, slamMap.geoAnchor, outputToNative]);

    // ── Auto-place template ───────────────────────────────────────────────────
    // Track the template identity so we can reset the "already placed" flag
    // atomically (inside the same effect) — eliminates the race condition that
    // occurred when the reset ran in a separate, later-declared effect.
    const prevTemplateKeyRef = useRef<string>('');

    useEffect(() => {
        // Build a key that changes whenever the template identity changes
        const key = `${templateImageUrl || ''}|${templateDimensions?.widthMeters ?? ''}|${templateDimensions?.heightMeters ?? ''}`;
        if (key !== prevTemplateKeyRef.current) {
            prevTemplateKeyRef.current = key;
            templateAutoPlacedRef.current = false;       // reset before the placement check
        }

        if (!slamLoaded || !templateDimensions || !templateImageUrl || mode !== 'polygon') {
            return;
        }
        if (hasDrawableGeom(existingGeom) || templateAutoPlacedRef.current) return;
        const { widthMeters, heightMeters } = templateDimensions;
        if (!widthMeters || !heightMeters) return;

        const wPx = widthMeters / mpp;
        const hPx = heightMeters / mpp;
        const slamW = slamImgRef.current?.naturalWidth ?? 800;
        const slamH = slamImgRef.current?.naturalHeight ?? 600;
        const cx = slamW / 2;
        const cy = slamH / 2;

        templateAutoPlacedRef.current = true;
        setPolygonPoints([
            { x: cx - wPx / 2, y: cy - hPx / 2 },
            { x: cx + wPx / 2, y: cy - hPx / 2 },
            { x: cx + wPx / 2, y: cy + hPx / 2 },
            { x: cx - wPx / 2, y: cy + hPx / 2 },
        ]);
    }, [slamLoaded, templateDimensions, templateImageUrl, mode, existingGeom, mpp]);

    // ── Auto-fit zoom when SLAM loads ─────────────────────────────────────────

    useEffect(() => {
        if (!slamLoaded || !slamImgRef.current || !containerRef.current) return;
        const cRect = containerRef.current.getBoundingClientRect();
        const natW = slamImgRef.current.naturalWidth;
        const natH = slamImgRef.current.naturalHeight;
        const fitZoom = Math.min(cRect.width / natW, cRect.height / natH, 1);
        setZoom(Math.max(MIN_ZOOM, fitZoom));
        // Center the image
        setPan({ x: (cRect.width - natW * fitZoom) / 2, y: (cRect.height - natH * fitZoom) / 2 });
    }, [slamLoaded]);

    // ── Canvas resize ─────────────────────────────────────────────────────────

    // Keep latest draw function in a ref so resizeCanvas can call it without
    // needing draw in its dependency array (which would re-subscribe the
    // ResizeObserver on every state change).
    const drawFnRef = useRef<() => void>(() => {});

    const resizeCanvas = useCallback(() => {
        const canvas = canvasRef.current;
        const container = containerRef.current;
        if (!canvas || !container) return;
        const rect = container.getBoundingClientRect();
        const dpr = window.devicePixelRatio || 1;
        canvas.width = rect.width * dpr;
        canvas.height = rect.height * dpr;
        canvas.style.width = `${rect.width}px`;
        canvas.style.height = `${rect.height}px`;
        // Setting canvas.width/height clears the canvas — redraw immediately
        // (the ResizeObserver fires asynchronously after React's render, so a
        // ref-flag approach misses the next render cycle and leaves the canvas blank)
        drawFnRef.current();
    }, []);

    useEffect(() => {
        resizeCanvas();
        const obs = new ResizeObserver(resizeCanvas);
        if (containerRef.current) obs.observe(containerRef.current);
        return () => obs.disconnect();
    }, [resizeCanvas]);

    // ── DRAW ──────────────────────────────────────────────────────────────────

    const draw = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        const dpr = window.devicePixelRatio || 1;

        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        // Apply DPR scaling, then our pan+zoom
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.translate(pan.x, pan.y);
        ctx.scale(zoom, zoom);

        // 1) Draw SLAM map background
        const slamImg = slamImgRef.current;
        if (slamImg && slamLoaded) {
            ctx.drawImage(slamImg, 0, 0);
        }

        // 2) Draw template image warped to polygon corners (perspective warp via triangle subdivision)
        const tplImg = templateImgRef.current;
        if (tplImg && templateLoaded && polygonPoints.length === 4) {
            const imgW = tplImg.naturalWidth;
            const imgH = tplImg.naturalHeight;
            // Corners order: 0=TL, 1=TR, 2=BR, 3=BL
            const [p0, p1, p2, p3] = polygonPoints;
            const N = WARP_SUBDIVISIONS;

            // Bilinear interpolation of quad corners
            const lerpQuad = (u: number, v: number): Pt => ({
                x: (1 - u) * (1 - v) * p0.x + u * (1 - v) * p1.x + u * v * p2.x + (1 - u) * v * p3.x,
                y: (1 - u) * (1 - v) * p0.y + u * (1 - v) * p1.y + u * v * p2.y + (1 - u) * v * p3.y,
            });

            // Draw an affine-mapped triangle: source (image coords) → destination (native coords)
            const drawWarpTriangle = (
                sx0: number, sy0: number, sx1: number, sy1: number, sx2: number, sy2: number,
                dx0: number, dy0: number, dx1: number, dy1: number, dx2: number, dy2: number,
            ) => {
                const det = sx0 * (sy1 - sy2) + sx1 * (sy2 - sy0) + sx2 * (sy0 - sy1);
                if (Math.abs(det) < 1e-10) return;

                ctx.save();
                ctx.beginPath();
                ctx.moveTo(dx0, dy0);
                ctx.lineTo(dx1, dy1);
                ctx.lineTo(dx2, dy2);
                ctx.closePath();
                ctx.clip();

                // Affine transform: image coords → native coords
                const a = (dx0 * (sy1 - sy2) + dx1 * (sy2 - sy0) + dx2 * (sy0 - sy1)) / det;
                const b = (dy0 * (sy1 - sy2) + dy1 * (sy2 - sy0) + dy2 * (sy0 - sy1)) / det;
                const c = (dx0 * (sx2 - sx1) + dx1 * (sx0 - sx2) + dx2 * (sx1 - sx0)) / det;
                const d = (dy0 * (sx2 - sx1) + dy1 * (sx0 - sx2) + dy2 * (sx1 - sx0)) / det;
                const e = (dx0 * (sx1 * sy2 - sx2 * sy1) + dx1 * (sx2 * sy0 - sx0 * sy2) + dx2 * (sx0 * sy1 - sx1 * sy0)) / det;
                const f = (dy0 * (sx1 * sy2 - sx2 * sy1) + dy1 * (sx2 * sy0 - sx0 * sy2) + dy2 * (sx0 * sy1 - sx1 * sy0)) / det;

                ctx.transform(a, b, c, d, e, f);
                ctx.drawImage(tplImg, 0, 0);
                ctx.restore();
            };

            ctx.globalAlpha = 0.6;
            for (let j = 0; j < N; j++) {
                for (let i = 0; i < N; i++) {
                    const u0 = i / N, u1 = (i + 1) / N;
                    const v0 = j / N, v1 = (j + 1) / N;

                    // Destination corners (native coords via bilinear interpolation)
                    const d00 = lerpQuad(u0, v0);
                    const d10 = lerpQuad(u1, v0);
                    const d11 = lerpQuad(u1, v1);
                    const d01 = lerpQuad(u0, v1);

                    // Source corners (image pixel coords)
                    const su0 = u0 * imgW, sv0 = v0 * imgH;
                    const su1 = u1 * imgW, sv1 = v1 * imgH;

                    // Triangle A: top-left → top-right → bottom-right
                    drawWarpTriangle(
                        su0, sv0, su1, sv0, su1, sv1,
                        d00.x, d00.y, d10.x, d10.y, d11.x, d11.y,
                    );
                    // Triangle B: top-left → bottom-right → bottom-left
                    drawWarpTriangle(
                        su0, sv0, su1, sv1, su0, sv1,
                        d00.x, d00.y, d11.x, d11.y, d01.x, d01.y,
                    );
                }
            }
            ctx.globalAlpha = 1;
        } else if (tplImg && templateLoaded && polygonPoints.length >= 3) {
            // Fallback for non-4-corner polygons: simple bounding box stretch
            ctx.save();
            ctx.beginPath();
            ctx.moveTo(polygonPoints[0].x, polygonPoints[0].y);
            for (let i = 1; i < polygonPoints.length; i++) {
                ctx.lineTo(polygonPoints[i].x, polygonPoints[i].y);
            }
            ctx.closePath();
            ctx.clip();
            const xs = polygonPoints.map(p => p.x);
            const ys = polygonPoints.map(p => p.y);
            const minX = Math.min(...xs), maxX = Math.max(...xs);
            const minY = Math.min(...ys), maxY = Math.max(...ys);
            ctx.globalAlpha = 0.6;
            ctx.drawImage(tplImg, minX, minY, maxX - minX, maxY - minY);
            ctx.globalAlpha = 1;
            ctx.restore();
        }

        // 3) Draw stored marker overlays
        for (const item of markerPoints) {
            const p = item.native;
            const isCorner = item.marker.categoryKey === 'area_corner';
            const r = (isCorner ? 7 : 5) / zoom;
            ctx.beginPath();
            ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
            ctx.fillStyle = isCorner ? '#1677ff' : '#13c2c2';
            ctx.fill();
            ctx.strokeStyle = '#ffffff';
            ctx.lineWidth = 2 / zoom;
            ctx.stroke();
            if (item.marker.label) {
                ctx.fillStyle = '#111827';
                ctx.font = `${Math.max(10, 11 / zoom)}px sans-serif`;
                ctx.textAlign = 'left';
                ctx.textBaseline = 'middle';
                ctx.fillText(String(item.marker.label), p.x + 10 / zoom, p.y);
            }
        }

        // 4) Draw polygon outline
        if (polygonPoints.length >= 2) {
            ctx.beginPath();
            ctx.moveTo(polygonPoints[0].x, polygonPoints[0].y);
            for (let i = 1; i < polygonPoints.length; i++) {
                ctx.lineTo(polygonPoints[i].x, polygonPoints[i].y);
            }
            if (polygonPoints.length >= 3) ctx.closePath();
            ctx.strokeStyle = '#1677ff';
            ctx.lineWidth = 2 / zoom;
            if (polygonPoints.length < 3) ctx.setLineDash([6 / zoom, 4 / zoom]);
            ctx.stroke();
            ctx.setLineDash([]);

            // Semi-transparent fill (only if no template image)
            if (polygonPoints.length >= 3 && !(tplImg && templateLoaded)) {
                ctx.fillStyle = 'rgba(22, 119, 255, 0.15)';
                ctx.fill();
            }
        }

        // 5) Draw corner handles
        const r = CORNER_RADIUS / zoom;
        for (let i = 0; i < polygonPoints.length; i++) {
            const p = polygonPoints[i];
            ctx.beginPath();
            ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
            ctx.fillStyle = draggingIdx === i ? '#ff4d4f' : '#1677ff';
            ctx.fill();
            ctx.strokeStyle = '#fff';
            ctx.lineWidth = 2 / zoom;
            ctx.stroke();

            // Label
            ctx.fillStyle = '#fff';
            ctx.font = `bold ${Math.max(10, 12 / zoom)}px sans-serif`;
            ctx.textAlign = 'left';
            ctx.textBaseline = 'bottom';
            ctx.shadowColor = '#000';
            ctx.shadowBlur = 3;
            ctx.fillText(`${i + 1}`, p.x + r + 2 / zoom, p.y - r);
            ctx.shadowBlur = 0;
        }

        // 6) Draw point pin
        if (mode === 'point' && pointLocation) {
            const pr = 8 / zoom;
            ctx.beginPath();
            ctx.arc(pointLocation.x, pointLocation.y, pr, 0, Math.PI * 2);
            ctx.fillStyle = '#1677ff';
            ctx.fill();
            ctx.strokeStyle = '#fff';
            ctx.lineWidth = 2 / zoom;
            ctx.stroke();
        }
    }, [pan, zoom, slamLoaded, templateLoaded, polygonPoints, pointLocation, draggingIdx, mode, markerPoints]);

    // Keep drawFnRef in sync so resizeCanvas always calls the latest draw
    drawFnRef.current = draw;

    // Redraw whenever state changes
    useEffect(() => { draw(); }, [draw]);

    useEffect(() => {
        if (!canDraw) setDraggingIdx(null);
    }, [canDraw]);

    // ── Mouse: find nearest corner ────────────────────────────────────────────

    const findCorner = useCallback((native: Pt): number | null => {
        const hitR = CORNER_HIT_RADIUS / zoom;
        for (let i = 0; i < polygonPoints.length; i++) {
            const dx = native.x - polygonPoints[i].x;
            const dy = native.y - polygonPoints[i].y;
            if (dx * dx + dy * dy <= hitR * hitR) return i;
        }
        return null;
    }, [polygonPoints, zoom]);

    // ── Mouse handlers ────────────────────────────────────────────────────────

    const handleMouseDown = useCallback((e: React.MouseEvent) => {
        // Middle button or Ctrl+left = pan
        if (e.button === 1 || (e.button === 0 && e.ctrlKey)) {
            e.preventDefault();
            setIsPanning(true);
            panStartRef.current = { x: e.clientX, y: e.clientY, panX: pan.x, panY: pan.y };
            return;
        }
        if (e.button !== 0) return;
        if (!canDraw) return;

        const native = screenToNative(e.clientX, e.clientY);
        const corner = findCorner(native);
        if (corner !== null) {
            e.preventDefault();
            setDraggingIdx(corner);
            return;
        }
    }, [screenToNative, findCorner, pan]);

    const handleMouseMove = useCallback((e: React.MouseEvent) => {
        if (isPanning) {
            const dx = e.clientX - panStartRef.current.x;
            const dy = e.clientY - panStartRef.current.y;
            setPan({ x: panStartRef.current.panX + dx, y: panStartRef.current.panY + dy });
            return;
        }
        if (draggingIdx !== null) {
            const native = screenToNative(e.clientX, e.clientY);
            setPolygonPoints(prev => prev.map((p, i) => i === draggingIdx ? native : p));
            return;
        }
        // Update cursor
        const canvas = canvasRef.current;
        if (canvas) {
            if (!canDraw) {
                canvas.style.cursor = 'default';
                return;
            }
            const native = screenToNative(e.clientX, e.clientY);
            const corner = findCorner(native);
            canvas.style.cursor = corner !== null ? 'grab' : (isTemplateLocked ? 'default' : 'crosshair');
        }
    }, [isPanning, draggingIdx, screenToNative, findCorner, isTemplateLocked, canDraw]);

    const handleMouseUp = useCallback((e: React.MouseEvent) => {
        if (isPanning) { setIsPanning(false); return; }
        if (draggingIdx !== null) { setDraggingIdx(null); return; }
    }, [isPanning, draggingIdx]);

    const handleClick = useCallback((e: React.MouseEvent) => {
        // Don't add points if we were panning or dragging
        if (isPanning || draggingIdx !== null) return;
        // Block adding points when template shape is locked
        if (isTemplateLocked) return;
        // Block drawing when canDraw is false (no area type selected)
        if (!canDraw) return;
        // Ignore clicks on buttons
        if ((e.target as HTMLElement).closest('button')) return;

        const native = screenToNative(e.clientX, e.clientY);
        const selectedMarker = markerPoints.find((item) => {
            const dx = native.x - item.native.x;
            const dy = native.y - item.native.y;
            return dx * dx + dy * dy <= Math.pow(14 / zoom, 2);
        });
        if (selectedMarker && onMarkerSelect) {
            onMarkerSelect(selectedMarker.marker);
            if (mode === 'point') {
                setPointLocation(selectedMarker.native);
            } else if (!isTemplateLocked && (!maxVertices || polygonPoints.length < maxVertices)) {
                setPolygonPoints(prev => [...prev, selectedMarker.native]);
            }
            return;
        }

        // Check if clicking on a corner (don't add new point)
        if (findCorner(native) !== null) return;

        if (mode === 'point') {
            setPointLocation(native);
        } else {
            // Enforce maxVertices limit
            if (maxVertices && polygonPoints.length >= maxVertices) return;
            setPolygonPoints(prev => [...prev, native]);
        }
    }, [isPanning, draggingIdx, isTemplateLocked, canDraw, screenToNative, findCorner, mode, maxVertices, polygonPoints.length, markerPoints, onMarkerSelect, zoom]);

    const handleWheel = useCallback((e: React.WheelEvent) => {
        e.preventDefault();
        const delta = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
        setZoom(z => {
            const newZ = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, z + delta));
            // Zoom toward mouse position
            const canvas = canvasRef.current;
            if (canvas) {
                const rect = canvas.getBoundingClientRect();
                const mx = e.clientX - rect.left;
                const my = e.clientY - rect.top;
                const ratio = newZ / z;
                setPan(p => ({
                    x: mx - ratio * (mx - p.x),
                    y: my - ratio * (my - p.y),
                }));
            }
            return newZ;
        });
    }, []);

    // Also handle global mouseup/mousemove for when cursor leaves the canvas
    useEffect(() => {
        if (draggingIdx === null && !isPanning) return;
        const onMove = (e: MouseEvent) => {
            if (isPanning) {
                const dx = e.clientX - panStartRef.current.x;
                const dy = e.clientY - panStartRef.current.y;
                setPan({ x: panStartRef.current.panX + dx, y: panStartRef.current.panY + dy });
            }
            if (draggingIdx !== null) {
                const canvas = canvasRef.current;
                if (!canvas) return;
                const rect = canvas.getBoundingClientRect();
                const sx = e.clientX - rect.left;
                const sy = e.clientY - rect.top;
                const native = { x: (sx - pan.x) / zoom, y: (sy - pan.y) / zoom };
                setPolygonPoints(prev => prev.map((p, i) => i === draggingIdx ? native : p));
            }
        };
        const onUp = () => { setDraggingIdx(null); setIsPanning(false); };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
        return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
    }, [draggingIdx, isPanning, zoom, pan]);

    // ── Zoom controls ─────────────────────────────────────────────────────────

    const handleZoomIn = useCallback(() => setZoom(z => Math.min(MAX_ZOOM, z + ZOOM_STEP)), []);
    const handleZoomOut = useCallback(() => setZoom(z => Math.max(MIN_ZOOM, z - ZOOM_STEP)), []);
    const handleZoomFit = useCallback(() => {
        if (!slamImgRef.current || !containerRef.current) return;
        const cRect = containerRef.current.getBoundingClientRect();
        const natW = slamImgRef.current.naturalWidth;
        const natH = slamImgRef.current.naturalHeight;
        const fitZ = Math.min(cRect.width / natW, cRect.height / natH, 1);
        setZoom(Math.max(MIN_ZOOM, fitZ));
        setPan({ x: (cRect.width - natW * fitZ) / 2, y: (cRect.height - natH * fitZ) / 2 });
    }, []);

    const handleUndoLastPoint = useCallback(() => setPolygonPoints(prev => prev.slice(0, -1)), []);
    const handleClear = useCallback(() => {
        setPolygonPoints([]);
        setPointLocation(null);
        templateAutoPlacedRef.current = false; // Allow template to re-auto-place
        onClear?.(); // Notify parent (e.g. to reset area type selection)
    }, [onClear]);

    // ── Coordinate label ──────────────────────────────────────────────────────

    const formatCoord = (p: Pt): string => {
        if (isAnchored && slamMap.geoAnchor) {
            const { mx, my } = pixelToMeters(p.x, p.y, mpp, anchorPx, anchorPy);
            const { lat, lng } = metersToGps(mx, my, slamMap.geoAnchor.lat, slamMap.geoAnchor.lng, slamMap.geoAnchor.bearingDegrees);
            return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
        }
        const { mx, my } = pixelToMeters(p.x, p.y, mpp, anchorPx, anchorPy);
        return `${mx.toFixed(2)}m, ${my.toFixed(2)}m`;
    };

    // ── Render ────────────────────────────────────────────────────────────────

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 8 }}>
            {/* Coordinate system info */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>SLAM map:</Text>
                <Text strong style={{ fontSize: 12 }}>{slamMap.name}</Text>
                {isAnchored
                    ? <Tag color="purple" style={{ fontSize: 10 }}>GPS anchored — coordinates in lat/lng</Tag>
                    : <Tag color="orange" style={{ fontSize: 10 }}>Unanchored — coordinates in metres</Tag>}
            </div>

            {/* Controls */}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                {mode === 'polygon' && polygonPoints.length > 0 && (
                    <>
                        {!isTemplateLocked && (
                            <Button size="small" icon={<UndoOutlined />} onClick={handleUndoLastPoint}>Undo last point</Button>
                        )}
                        <Button size="small" danger icon={<DeleteOutlined />} onClick={handleClear}>Clear</Button>
                        {polygonPoints.length >= 3
                            ? <Tag color="green">{polygonPoints.length} points — polygon ready</Tag>
                            : <Tag color="orange">{polygonPoints.length} point{polygonPoints.length !== 1 ? 's' : ''} — need {3 - polygonPoints.length} more</Tag>}
                        {isTemplateLocked && <Tag color="blue" style={{ fontSize: 10 }}>🔒 Template shape — drag corners to adjust</Tag>}
                    </>
                )}
                {mode === 'point' && pointLocation && (
                    <Button size="small" danger icon={<DeleteOutlined />} onClick={handleClear}>Clear pin</Button>
                )}
                <Tag style={{ fontSize: 10, marginLeft: 'auto' }}>{Math.round(zoom * 100)}%</Tag>
            </div>

            {/* Canvas container */}
            <div ref={containerRef} style={{
                flex: 1, minHeight: 400, position: 'relative',
                background: '#1a1a2e', borderRadius: 8, overflow: 'hidden',
            }}>
                <canvas
                    ref={canvasRef}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onClick={handleClick}
                    onWheel={handleWheel}
                    onContextMenu={e => e.preventDefault()}
                    style={{ display: 'block', width: '100%', height: '100%' }}
                />

                {/* Zoom controls overlay */}
                <div style={{
                    position: 'absolute', top: 8, right: 8, zIndex: 10,
                    display: 'flex', flexDirection: 'column', gap: 4,
                }}>
                    <Button size="small" icon={<ZoomInOutlined />} onClick={handleZoomIn} style={{ width: 32, height: 32, padding: 0 }} />
                    <Button size="small" icon={<ZoomOutOutlined />} onClick={handleZoomOut} style={{ width: 32, height: 32, padding: 0 }} />
                    <Button size="small" icon={<ExpandOutlined />} onClick={handleZoomFit} style={{ width: 32, height: 32, padding: 0 }} title="Fit to view" />
                </div>

                {/* Instruction overlays */}
                {/* Blocked overlay — shown when no area type is selected */}
                {!canDraw && polygonPoints.length === 0 && slamLoaded && (
                    <div style={{
                        position: 'absolute', bottom: 12, left: '50%', transform: 'translateX(-50%)',
                        background: 'rgba(250,140,22,0.85)', color: '#fff',
                        padding: '6px 20px', borderRadius: 12, fontSize: 12, pointerEvents: 'none',
                        whiteSpace: 'nowrap', zIndex: 10, fontWeight: 500,
                    }}>⚠ Select an area type before drawing on the map</div>
                )}
                {canDraw && mode === 'polygon' && polygonPoints.length === 0 && slamLoaded && (
                    <div style={{
                        position: 'absolute', bottom: 12, left: '50%', transform: 'translateX(-50%)',
                        background: 'rgba(0,0,0,0.65)', color: '#fff',
                        padding: '4px 16px', borderRadius: 12, fontSize: 12, pointerEvents: 'none',
                        whiteSpace: 'nowrap', zIndex: 10,
                    }}>Click on the map to draw the area polygon • Scroll to zoom • Ctrl+drag to pan</div>
                )}
                {canDraw && mode === 'point' && !pointLocation && slamLoaded && (
                    <div style={{
                        position: 'absolute', bottom: 12, left: '50%', transform: 'translateX(-50%)',
                        background: 'rgba(0,0,0,0.65)', color: '#fff',
                        padding: '4px 16px', borderRadius: 12, fontSize: 12, pointerEvents: 'none',
                        whiteSpace: 'nowrap', zIndex: 10,
                    }}>Click on the map to place a location pin • Scroll to zoom • Ctrl+drag to pan</div>
                )}
            </div>

        </div>
    );
};

export default IndoorAreaMap;
